import { Link } from "react-router";
import { Book, Package, Layers } from "lucide-react";

export function MaterialsLibraryPage() {
  return (
    <div className="min-h-screen">
      <section className="py-20 bg-gradient-to-br from-background via-gray-50 to-green-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-8">
            <Book className="size-4" />
            <span>Master Options Guide</span>
          </div>
          <h1 className="text-6xl font-bold mb-6">Packaging Materials Library</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Comprehensive guide to packaging materials, substrates, and board types. Choose the perfect material for your packaging needs.
          </p>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-6">Paperboard Materials</h2>
            <p className="text-xl text-muted-foreground mb-12">
              Premium paperboard options for folding cartons, insert cards, and retail packaging
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Link to="/materials/sbs-c1s" className="group bg-gradient-to-br from-background to-gray-50 rounded-2xl p-8 border border-border hover:shadow-2xl hover:border-accent/50 transition-all">
                <div className="icon-box size-20 shadow-md mb-6">
                  <Layers className="size-10" />
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-accent transition-colors">SBS C1S (Coated One Side)</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Premium solid bleached sulfate board with one-sided coating. Ideal for printed packaging with white interior.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Weight Range</div>
                    <div className="font-semibold">200-400 gsm</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Finish</div>
                    <div className="font-semibold">Glossy</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">Excellent Print</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">FSC Available</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">Food Safe</span>
                </div>

              </Link>

              <Link to="/materials/sbs-c2s" className="group bg-gradient-to-br from-background to-gray-50 rounded-2xl p-8 border border-border hover:shadow-2xl hover:border-accent/50 transition-all">
                <div className="icon-box size-20 shadow-md mb-6">
                  <Layers className="size-10" />
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-accent transition-colors">SBS C2S (Coated Two Sides)</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Double-sided coating for premium packaging requiring print on both sides. Perfect for high-end retail boxes.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Weight Range</div>
                    <div className="font-semibold">250-450 gsm</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Finish</div>
                    <div className="font-semibold">Glossy Both</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">Premium Quality</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">Double Print</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">Bright White</span>
                </div>

              </Link>

              <Link to="/materials/kraft" className="group bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-8 border border-border hover:shadow-2xl hover:border-accent/50 transition-all">
                <div className="size-20 bg-amber-100 shadow-md rounded-xl flex items-center justify-center mb-6">
                  <Layers className="size-10 text-amber-700" />
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-accent transition-colors">Kraft Board</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Natural brown kraft paperboard for eco-conscious brands. Rustic, authentic appeal with environmental benefits.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-3 bg-white/50 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Weight Range</div>
                    <div className="font-semibold">250-450 gsm</div>
                  </div>
                  <div className="p-3 bg-white/50 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Color</div>
                    <div className="font-semibold">Natural Brown</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">Eco-Friendly</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">100% Recycled</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">Natural Look</span>
                </div>

              </Link>

              <Link to="/materials/duplex" className="group bg-gradient-to-br from-background to-gray-50 rounded-2xl p-8 border border-border hover:shadow-2xl hover:border-accent/50 transition-all">
                <div className="size-20 bg-gray-200 shadow-md rounded-xl flex items-center justify-center mb-6">
                  <Layers className="size-10 text-foreground/70" />
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-accent transition-colors">Duplex Board</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  White top layer with gray back. Cost-effective solution for packaging where interior color is not critical.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Weight Range</div>
                    <div className="font-semibold">250-400 gsm</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Layers</div>
                    <div className="font-semibold">White/Gray</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">Cost-Effective</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">Good Print</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">Versatile</span>
                </div>

              </Link>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-6">Corrugated Materials</h2>
            <p className="text-xl text-muted-foreground mb-12">
              Durable corrugated board for shipping boxes and protective packaging
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Link to="/materials/corrugated-kraft" className="group bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-border hover:shadow-2xl hover:border-accent/50 transition-all">
                <div className="size-20 bg-amber-100 shadow-md rounded-xl flex items-center justify-center mb-6">
                  <Package className="size-10 text-amber-700" />
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-accent transition-colors">Kraft Corrugated</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Natural kraft corrugated board for e-commerce and shipping. Available in multiple flute types.
                </p>


              </Link>

              <Link to="/materials/corrugated-white" className="group bg-gradient-to-br from-background to-gray-50 rounded-2xl p-8 border border-border hover:shadow-2xl hover:border-accent/50 transition-all">
                <div className="icon-box size-20 shadow-md mb-6">
                  <Package className="size-10" />
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-accent transition-colors">White Corrugated</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Premium white-lined corrugated for branded e-commerce packaging with high-quality print.
                </p>


              </Link>
            </div>
          </div>

          <div>
            <h2 className="text-4xl font-bold mb-6">Rigid Box Materials</h2>
            <p className="text-xl text-muted-foreground mb-12">
              Premium materials for luxury rigid box construction
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Link to="/materials/chipboard" className="group bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-8 border border-border hover:shadow-2xl hover:border-accent/50 transition-all">
                <div className="size-20 bg-gray-300 shadow-md rounded-xl flex items-center justify-center mb-6">
                  <Layers className="size-10 text-foreground/80" />
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-accent transition-colors">Chipboard</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  High-density chipboard core for rigid box construction. Provides exceptional structural integrity.
                </p>


              </Link>

              <Link to="/materials/wrapped-materials" className="group bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-border hover:shadow-2xl hover:border-accent/50 transition-all">
                <div className="size-20 bg-purple-100 shadow-md rounded-xl flex items-center justify-center mb-6">
                  <Package className="size-10 text-purple-700" />
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-accent transition-colors">Wrapped Materials</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Premium papers and fabrics for wrapping chipboard. Create luxurious finishes for rigid boxes.
                </p>


              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-accent text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Need Help Choosing Materials?</h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Our packaging experts can guide you to the perfect material for your specific needs
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/quote"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-accent rounded-xl font-medium transition-colors hover:bg-gray-100 shadow-lg"
            >
              Get Expert Consultation
            </Link>
            <a
              href="#"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 rounded-xl font-medium backdrop-blur-sm border border-white/20"
            >
              Download Materials Guide
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
